import React, { useMemo, useState } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { View, Text, Image, StyleSheet } from './rn';
import { mediaUrl } from '../services/api';

// Fotoğrafı olmayan kullanıcılar için: isimden türetilmiş, sabit renkli baş harf
// rozeti. Renk isimden hash'lenir — aynı kullanıcı her zaman aynı rengi alır.
const PALETTE = [
  ['#F94144', '#000'], ['#F3722C', '#000'], ['#F8961E', '#000'], ['#F9C74F', '#000'], ['#90BE6D', '#000'],
  ['#43AA8B', '#000'], ['#4D908E', '#000'], ['#577590', '#fff'], ['#277DA1', '#fff'], ['#9C6ADE', '#000'],
] as const;

function colorsFor(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) % 0x100000000;
  return PALETTE[hash % PALETTE.length];
}

/**
 * D-408 — ismin ilk GÖRÜNEN karakteri.
 *
 * `charAt(0)` bir UTF-16 kod birimi döndürür. Emoji, birçok CJK genişletme
 * karakteri ve bayraklar iki kod biriminden (vekil çift) oluştuğu için
 * `charAt(0)` çiftin yarısını verir ve ekranda "" görünür. "👨‍👩‍👧 Aile"
 * ismi rozette kırık bir kare olarak çıkıyordu.
 *
 * `Intl.Segmenter` varsa grafem kümesi alınır: birleşen aksanlar (e + U+0301),
 * ZWJ ile birleştirilmiş emoji aileleri ve bayraklar tek görsel birim sayılır.
 * Segmenter yoksa ilk kod noktası ve bağlı işaretler korunur; bu yedek yol
 * bütün ZWJ/emoji kümelerini bölümlediği iddiasını taşımaz.
 *
 * Büyük harfe çevirme ÖNCE yapılır: "ß".toUpperCase() "SS" verir ve rozete iki
 * harf sığdırırdı; grafem sonra alınınca tek harf kalır.
 */
export function firstGrapheme(value: string): string {
  const trimmed = value.trim().normalize('NFC');
  if (trimmed === '') return '';
  const upper = trimmed.toUpperCase();
  const segmenter = (Intl as any)?.Segmenter;
  if (typeof segmenter === 'function') {
    try {
      const [first] = new segmenter(undefined, { granularity: 'grapheme' }).segment(upper);
      if (first?.segment) return first.segment;
    } catch {
      // Segmenter var ama bu ortamda kullanılamıyorsa aşağıdaki yola düş.
    }
  }
  return upper.match(/^\P{M}\p{M}*/u)?.[0] ?? '';
}

type Props = {
  photoUrl?: string | null;
  name?: string | null;
  size: number;
  style?: StyleProp<ViewStyle>;
  /**
   * D-666 — konuşmanın duygusundan gelen zemin ve baş harf rengi. Yalnız
   * fotoğrafı olmayan kişide görünür; fotoğraf her zaman kazanır. Yoksa isim
   * rengi (D-325) kalır.
   */
  tint?: readonly [string, string] | null;
};

// Her URL kendi yükleme ömrünü taşır. A → B → A sırasında eski A'nın geç
// callback'i yeni A'nın fotoğrafını gizleyemez veya erken görünür yapamaz.
function AvatarPhoto({ uri, fallback }: { uri: string; fallback: React.ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'failed'>('loading');

  return (
    <>
      {status !== 'loaded' && fallback}
      {status !== 'failed' && (
        <Image
          source={{ uri }}
          testID="avatar-photo"
          style={[StyleSheet.absoluteFill, status === 'loaded' ? styles.visible : styles.hidden]}
          resizeMode="cover"
          fadeDuration={0}
          onLoad={() => setStatus((previous) => previous === 'failed' ? previous : 'loaded')}
          onError={() => setStatus('failed')}
        />
      )}
    </>
  );
}

export default function Avatar({ photoUrl, name, size, style, tint }: Props) {
  const dim = useMemo(() => ({ width: size, height: size, borderRadius: size / 2 }), [size]);
  // Grafem seçimi ile URL başına yükleme ömrü ve kontrast aynı anda korunur.
  const initial = firstGrapheme(name ?? '') || '?';
  // Zeminler temadan bağımsızdır; her birinin siyah/beyaz ön planı en az
  // 4,5:1 kontrastı korur. Kullanıcının isimden gelen rengi değişmez.
  const [background, foreground] = tint ?? colorsFor(name || '?');
  const uri = mediaUrl(photoUrl?.trim());
  const fallback = (
    <View testID="avatar-initial" style={[StyleSheet.absoluteFill, styles.frame, { backgroundColor: background }]}>
      <Text allowFontScaling={false} numberOfLines={1} style={[styles.initial, { color: foreground, fontSize: size * 0.42 }]}>
        {initial}
      </Text>
    </View>
  );

  return (
    <View style={[dim, style, styles.frame]}>
      {uri ? <AvatarPhoto key={uri} uri={uri} fallback={fallback} /> : fallback}
    </View>
  );
}

const styles = StyleSheet.create({
  frame: { alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  initial: { fontWeight: '700' },
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
});
